import type { InventoryItem, ItemCondition, MarketplaceListing, Money } from "./types";
import { conditionLabels } from "./labels";

export interface EbayCategoryChoice { id: string; name: string; path: string }
export interface EbayAspectChoice {
  name: string; required: boolean; selectionOnly: boolean; multiple: boolean; maxLength: number;
  values: Array<{ value: string; constraints: Array<{ name: string; values: string[] }> }>;
}
export interface EbayPublicationRequirements {
  category: EbayCategoryChoice; conditionRequired: boolean;
  conditions: Array<{ id: string; name: string; help?: string; needsDescriptors: boolean }>;
  aspects: EbayAspectChoice[];
}
export interface EbayPublicationSelling {
  mode?: "listing" | "policies";
  listingTerms?: EbayListingTerms;
  paymentPolicyID?: string; fulfillmentPolicyID?: string; returnPolicyID?: string; postalCode?: string;
}
/** Explicit terms for one listing; never enrolls the seller in business policies. */
export interface EbayListingTerms {
  serviceCode?: string; handlingDays?: number; postage?: string; additionalPostage?: string;
  acceptsReturns?: boolean; returnDays?: number; returnPayer?: string; immediatePay?: boolean;
  customDeliveryConfirmed?: boolean;
}
export interface EbayListingTermsOptions {
  sellerType: "private" | "business";
  services: EbayPolicyOptions["services"];
  handlingDays: EbayPolicyOptions["handlingDays"];
  returns: EbayPolicyOptions["returns"];
}
export interface EbayListingTermsReview {
  terms: EbayListingTerms; sellerType: EbayListingTermsOptions["sellerType"];
  summary: { delivery: string[]; returns: string[]; payment: string[] };
}
export type EbayPolicyKind = "fulfillment" | "return" | "payment";
export interface EbayPolicyChoice {
  id: string; name: string;
  /** Full native terms are retained; summaries are only a reading aid. */
  terms?: { fingerprint: string; summary: string[]; details: Array<{ label: string; value: string }> };
}
export interface EbayPolicyOptions {
  canCreate: boolean;
  deliveryCreation: "supported" | "on_ebay";
  sellerType?: "private" | "business";
  services: Array<{ code: string; name: string; carrier: string }>;
  handlingDays: Array<{ days: number; name: string }>;
  returns: { periods: number[]; payers: string[]; applicable: boolean };
}
export interface EbayPolicyDraft {
  kind: EbayPolicyKind; name: string; categoryID: string;
  serviceCode?: string; handlingDays?: number; postage?: string; additionalPostage?: string;
  acceptsReturns?: boolean; returnDays?: number; returnPayer?: string; immediatePay?: boolean;
}
export interface EbayPolicyPreview { draft: EbayPolicyDraft; name: string; fingerprint: string; terms: NonNullable<EbayPolicyChoice["terms"]> }
export interface EbayPolicyResult { state: "complete" | "pending" | "not_started" | "rejected"; policy?: EbayPolicyChoice; message?: string }
export interface EbayPublicationSetup {
  /** Policy availability only: per-listing terms do not require enrolment. */
  status: "ready" | "policies_not_enabled" | "policies_unavailable" | "policies_unknown";
  choices: { paymentPolicies: EbayPolicyChoice[]; fulfillmentPolicies: EbayPolicyChoice[]; returnPolicies: EbayPolicyChoice[] };
  defaults: EbayPublicationSelling;
  canCreatePolicies?: boolean;
  canManagePolicies?: boolean;
}
export interface EbayPublicationSelection { categoryID: string; conditionID?: string; specifics: Record<string, string[]>; selling?: EbayPublicationSelling; rememberSelling?: boolean }
export interface EbayPublicationView {
  id: string; state: "review" | "pending" | "published" | "rejected"; expiresAt: string; externalURL?: string;
  details: {
    title: string; description: string; price: Money; quantity: number; categoryID: string; categoryName: string;
    conditionID?: string; conditionName?: string; specifics: Record<string, string[]>; photoURLs: string[];
    postalCode: string; paymentPolicyID?: string; fulfillmentPolicyID?: string; returnPolicyID?: string;
    policyNames: { payment: string; delivery: string; returns: string }; sellerName: string;
    listingTerms?: EbayListingTermsReview;
    policies?: { payment: EbayPolicyChoice; delivery: EbayPolicyChoice; returns: EbayPolicyChoice };
  };
  fees: Array<{ name: string; amount: Money }>; warnings: string[];
}
/** Incomplete answers are saved separately from the reviewed publication. */
export interface EbayPublicationDraft {
  version: string; accountID: string; sellerID: string; selection: EbayPublicationSelection; updatedAt: string;
}
export interface EbayPublicationDraftSave {
  expectedVersion: string | null; mutationID: string; selection: EbayPublicationSelection;
}
export interface EbayPublicationRead {
  intent?: EbayPublicationView; draft?: EbayPublicationDraft; draftAccountChanged?: boolean; reviewCurrent?: boolean;
  writeAccess: "ready" | "needs_permission" | "unavailable";
}

const normalized = (value: string) => value.trim().toLocaleLowerCase("en-GB").replace(/\s+/g, " ");
const sizeNames = [["xs", "extra small"], ["s", "small"], ["m", "medium"], ["l", "large"], ["xl", "extra large"], ["xxl", "2xl", "extra extra large"]];

/** Only saved item facts, never a title guess or unaccepted research suggestion. */
export function ebayAspectItemValue(item: InventoryItem | undefined, name: string): string | undefined {
  if (!item) return undefined;
  const fields: Record<string, string | undefined> = { brand: item.brand, size: item.size, colour: item.color, color: item.color, material: item.material, "outer shell material": item.material };
  return fields[normalized(name)]?.trim() || undefined;
}

/** Match the selected category's vocabulary without inventing required details. */
export function prefillEbayPublicationSelection(requirements: EbayPublicationRequirements, item: InventoryItem | undefined, listing: Pick<MarketplaceListing, "categoryExternalID" | "conditionExternalValue">, previous?: EbayPublicationSelection): EbayPublicationSelection {
  const specifics: Record<string, string[]> = {};
  for (const aspect of requirements.aspects) {
    const saved = previous?.specifics[aspect.name];
    const value = ebayAspectItemValue(item, aspect.name);
    // Returning to the same category must not refill an optional field the
    // seller left out of a saved review (empty specifics are omitted there).
    const raw = saved ?? (previous?.categoryID === requirements.category.id ? [] : value ? [value] : []);
    if (raw.length > (aspect.multiple ? 30 : 1)) continue;
    const values = raw.flatMap((entry) => {
      const text = entry.trim();
      if (!text || text.length > aspect.maxLength) return [];
      const exact = aspect.values.filter((choice) => normalized(choice.value) === normalized(text));
      if (exact.length === 1) return [exact[0].value];
      if (normalized(aspect.name) === "size") {
        const names = sizeNames.find((group) => group.includes(normalized(text)));
        const matches = names && aspect.values.filter((choice) => names.includes(normalized(choice.value)));
        if (matches?.length === 1) return [matches[0].value];
      }
      return aspect.selectionOnly ? [] : [text];
    });
    if (values.length || saved?.length === 0) specifics[aspect.name] = [...new Set(values)];
  }
  // Dependencies can themselves be constrained. Remove unsupported values until
  // stable rather than filling assumptions such as Size Type = Regular.
  let changed = true;
  while (changed) {
    changed = false;
    for (const aspect of requirements.aspects) {
      const values = specifics[aspect.name];
      if (!values?.length) continue;
      const allowed = values.filter((value) => !aspect.values.find((choice) => choice.value === value)?.constraints.some((constraint) => !specifics[constraint.name]?.some((selected) => constraint.values.includes(selected))));
      if (allowed.length !== values.length) { specifics[aspect.name] = allowed; changed = true; }
    }
  }
  const previousCondition = previous?.categoryID === requirements.category.id ? requirements.conditions.find((choice) => choice.id === previous.conditionID) : undefined;
  const listingCondition = listing.categoryExternalID === requirements.category.id ? listing.conditionExternalValue?.trim() : undefined;
  const listingConditionLabel = listingCondition && Object.prototype.hasOwnProperty.call(conditionLabels, listingCondition) ? conditionLabels[listingCondition as ItemCondition] : listingCondition;
  const knownCondition = listingConditionLabel || (item && item.condition !== "unknown" ? conditionLabels[item.condition] : undefined);
  const matches = knownCondition ? requirements.conditions.filter((choice) => !choice.needsDescriptors && (normalized(choice.name) === normalized(knownCondition) || choice.id === listingCondition)) : [];
  const conditionID = previousCondition?.id ?? (matches.length === 1 ? matches[0].id : undefined);
  return { categoryID: requirements.category.id, ...(conditionID ? { conditionID } : {}), specifics };
}
